import _extends from "../../../../@babel/runtime/helpers/esm/extends.es.js";
import * as React from "react";
import PropTypes from "../../../../prop-types/index.es.js";
import { useGridPrivateApiContext } from "../../hooks/utils/useGridPrivateApiContext.es.js";
import { useGridSelector } from "../../hooks/utils/useGridSelector.es.js";
import { GridMainContainer } from "../containers/GridMainContainer.es.js";
import { useGridRootProps } from "../../hooks/utils/useGridRootProps.es.js";
import { gridVisibleColumnDefinitionsSelector, gridColumnPositionsSelector, gridColumnVisibilityModelSelector } from "../../hooks/features/columns/gridColumnsSelector.es.js";
import { gridFilterActiveItemsLookupSelector } from "../../hooks/features/filter/gridFilterSelector.es.js";
import { gridSortColumnLookupSelector } from "../../hooks/features/sorting/gridSortingSelector.es.js";
import { gridTabIndexColumnHeaderSelector, gridTabIndexCellSelector, unstable_gridTabIndexColumnGroupHeaderSelector, gridFocusColumnHeaderSelector, unstable_gridFocusColumnGroupHeaderSelector } from "../../hooks/features/focus/gridFocusStateSelector.es.js";
import { gridDensityFactorSelector } from "../../hooks/features/density/densitySelector.es.js";
import { gridColumnGroupsHeaderMaxDepthSelector, gridColumnGroupsHeaderStructureSelector } from "../../hooks/features/columnGrouping/gridColumnGroupsSelector.es.js";
import { gridColumnMenuSelector } from "../../hooks/features/columnMenu/columnMenuSelector.es.js";
import { j as jsxRuntimeExports } from "../../../../react/jsx-runtime.es.js";
import useEnhancedEffect from "../../../utils/useEnhancedEffect/useEnhancedEffect.es.js";
function GridBody(props) {
  const {
    VirtualScrollerComponent,
    ColumnHeadersProps,
    children
  } = props;
  const apiRef = useGridPrivateApiContext();
  const rootProps = useGridRootProps();
  const rootRef = React.useRef(null);
  const visibleColumns = useGridSelector(apiRef, gridVisibleColumnDefinitionsSelector);
  const filterColumnLookup = useGridSelector(apiRef, gridFilterActiveItemsLookupSelector);
  const sortColumnLookup = useGridSelector(apiRef, gridSortColumnLookupSelector);
  const columnPositions = useGridSelector(apiRef, gridColumnPositionsSelector);
  const columnHeaderTabIndexState = useGridSelector(apiRef, gridTabIndexColumnHeaderSelector);
  const cellTabIndexState = useGridSelector(apiRef, gridTabIndexCellSelector);
  const columnGroupHeaderTabIndexState = useGridSelector(apiRef, unstable_gridTabIndexColumnGroupHeaderSelector);
  const columnHeaderFocus = useGridSelector(apiRef, gridFocusColumnHeaderSelector);
  const columnGroupHeaderFocus = useGridSelector(apiRef, unstable_gridFocusColumnGroupHeaderSelector);
  const densityFactor = useGridSelector(apiRef, gridDensityFactorSelector);
  const headerGroupingMaxDepth = useGridSelector(apiRef, gridColumnGroupsHeaderMaxDepthSelector);
  const columnMenuState = useGridSelector(apiRef, gridColumnMenuSelector);
  const columnVisibility = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
  const columnGroupsHeaderStructure = useGridSelector(apiRef, gridColumnGroupsHeaderStructureSelector);
  const hasOtherElementInTabSequence = !(columnGroupHeaderTabIndexState === null && columnHeaderTabIndexState === null && cellTabIndexState === null);
  useEnhancedEffect(() => {
    apiRef.current.computeSizeAndPublishResizeEvent();
    const elementToObserve = rootRef.current;
    if (typeof ResizeObserver === "undefined") {
      return () => {
      };
    }
    let animationFrame;
    const observer = new ResizeObserver(() => {
      animationFrame = requestAnimationFrame(() => {
        apiRef.current.computeSizeAndPublishResizeEvent();
      });
    });
    if (elementToObserve) {
      observer.observe(elementToObserve);
    }
    return () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
      if (elementToObserve) {
        observer.unobserve(elementToObserve);
      }
    };
  }, [apiRef]);
  const columnHeadersRef = React.useRef(null);
  const columnsContainerRef = React.useRef(null);
  const virtualScrollerRef = React.useRef(null);
  apiRef.current.register("private", {
    columnHeadersContainerElementRef: columnsContainerRef,
    columnHeadersElementRef: columnHeadersRef,
    virtualScrollerRef,
    mainElementRef: rootRef
  });
  const hasDimensions = !!apiRef.current.getRootDimensions();
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(GridMainContainer, {
    ref: rootRef,
    children: [/* @__PURE__ */ jsxRuntimeExports.jsx(rootProps.slots.columnHeaders, _extends({
      ref: columnsContainerRef,
      innerRef: columnHeadersRef,
      visibleColumns,
      filterColumnLookup,
      sortColumnLookup,
      columnPositions,
      columnHeaderTabIndexState,
      columnGroupHeaderTabIndexState,
      columnHeaderFocus,
      columnGroupHeaderFocus,
      densityFactor,
      headerGroupingMaxDepth,
      columnMenuState,
      columnVisibility,
      columnGroupsHeaderStructure,
      hasOtherElementInTabSequence
    }, ColumnHeadersProps)), hasDimensions && /* @__PURE__ */ jsxRuntimeExports.jsx(
      VirtualScrollerComponent,
      {
        ref: virtualScrollerRef
      }
    ), children]
  });
}
process.env.NODE_ENV !== "production" ? GridBody.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "yarn proptypes"  |
  // ----------------------------------------------------------------------
  children: PropTypes.node,
  ColumnHeadersProps: PropTypes.object,
  VirtualScrollerComponent: PropTypes.elementType.isRequired
} : void 0;
export {
  GridBody
};
//# sourceMappingURL=GridBody.es.js.map
